import { useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8003';

/**
 * Overlay VIIRS Night Lights tiles on a Google Map instance.
 * Fetches tile URL from backend Earth Engine endpoint, then adds
 * a google.maps.ImageMapType as an overlay with configurable opacity.
 */
const NightLightsLayer = ({ map, opacity = 0.6, visible = true }) => {
  const overlayRef = useRef(null);
  const [tileUrl, setTileUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch tile URL from backend
  useEffect(() => {
    let cancelled = false;
    const fetchTiles = async () => {
      try {
        setLoading(true);
        setError(null);
        const token = localStorage.getItem('mipa_token');
        const res = await axios.get(`${API_BASE_URL}/earth-engine/night-lights-tiles`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!cancelled && res.data?.tile_url) {
          setTileUrl(res.data.tile_url);
        }
      } catch (err) {
        if (!cancelled) {
          const msg = err.response?.status === 503
            ? 'Night Lights layer unavailable (Earth Engine not configured)'
            : 'Failed to load Night Lights tiles';
          setError(msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchTiles();
    return () => { cancelled = true; };
  }, []);

  // Add / remove overlay when tileUrl, map, or visibility changes
  useEffect(() => {
    if (!map || !tileUrl || !visible) {
      if (overlayRef.current) {
        map?.overlayMapTypes?.removeAt(
          map.overlayMapTypes.indexOf(overlayRef.current)
        );
        overlayRef.current = null;
      }
      return;
    }

    const nightLayer = new window.google.maps.ImageMapType({
      getTileUrl: (coord, zoom) =>
        tileUrl.replace('{z}', zoom).replace('{x}', coord.x).replace('{y}', coord.y),
      tileSize: new window.google.maps.Size(256, 256),
      opacity,
      name: 'NightLights',
    });

    map.overlayMapTypes.push(nightLayer);
    overlayRef.current = nightLayer;

    return () => {
      if (overlayRef.current && map?.overlayMapTypes) {
        const idx = map.overlayMapTypes.indexOf(overlayRef.current);
        if (idx !== -1) map.overlayMapTypes.removeAt(idx);
        overlayRef.current = null;
      }
    };
  }, [map, tileUrl, visible, opacity]);

  // Update opacity on existing overlay without re-adding
  useEffect(() => {
    if (overlayRef.current) {
      overlayRef.current.setOpacity(opacity);
    }
  }, [opacity]);

  if (loading) {
    return (
      <div style={{
        position: 'absolute', bottom: 60, right: 12,
        background: 'rgba(0,0,0,0.7)', color: '#fff',
        padding: '6px 12px', borderRadius: 8, fontSize: 12, zIndex: 15,
      }}>
        Loading Night Lights...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        position: 'absolute', bottom: 60, right: 12,
        background: 'rgba(220,38,38,0.85)', color: '#fff',
        padding: '6px 12px', borderRadius: 8, fontSize: 12, zIndex: 15,
      }}>
        {error}
      </div>
    );
  }

  return null;
};

export default NightLightsLayer;
